import { ref } from 'vue'
import type { AppArea } from '@/funnels/@types'

// Everything the /fwf/* → parent-funnel handoff has to carry across the route change.
//
// A FwF pre-funnel is authored by cloning the parent's ENTRY screens and re-contenting
// them ("smart crossbreeding"), then hands the user off with an in-app router push. Two
// things follow from that and both are this module's job:
//
//   1. the parent must NOT replay the cloned screens — it would show them twice;
//   2. the parent must NOT ask the age again, and must be able to READ the answer the
//      pre-funnel already collected.
//
// State lives at module level rather than in Pinia on purpose: it must survive the route
// change (two different views read and write it) but must NOT be persisted — a hard
// reload clears it, which is the safe default, so a later direct visit to the parent
// funnel never silently hides screens or skips its age screen.

interface HandoffState {
	/** appArea the pre-funnel hands off to. Null when no handoff is pending. */
	appArea: AppArea | null
	/** Parent `urlSlug`s the pre-funnel cloned, to drop from the parent's config. */
	hiddenScreens: string[]
	/** The collected age in the parent's own bucket vocabulary — see `ageForConfig`. */
	ageForParent: string | null
}

const EMPTY: HandoffState = { appArea: null, hiddenScreens: [], ageForParent: null }

const state = ref<HandoffState>({ ...EMPTY })

export function useFwfHandoff() {
	/**
	 * Arm the handoff. Called by `FwfView` for a pre-funnel that will actually render,
	 * with the destination's appArea and the entry's `hiddenParentScreens` — and again
	 * if the completion answer settles on a different destination.
	 *
	 * Keyed by appArea, NOT by funnel id: an experiment can swap `funnel.id` for the same
	 * appArea, and the FwF bootstrap resolves without an ABSmartly context so it only ever
	 * knows the base id. The appArea is what the registry names and is stable across those
	 * variants — keying on the id would silently no-op the dedup for any bucketed user.
	 */
	function beginHandoff(appArea: AppArea, hiddenScreens?: string[] | null): void {
		state.value = {
			appArea,
			hiddenScreens: (hiddenScreens ?? []).filter((slug) => typeof slug === 'string' && slug.trim().length > 0),
			ageForParent: null,
		}
	}

	/**
	 * Disarm it — the pre-funnel never rendered (disabled entry, failed config load or an
	 * unknown slug), so the parent is a normal fresh visit and must behave like one.
	 */
	function endHandoff(): void {
		state.value = { ...EMPTY }
	}

	/**
	 * True once a pre-funnel has collected the age. Every FwF opens with its own age gate,
	 * so an armed handoff IS a collected age — no need to inspect the config for one.
	 *
	 * Read by the `hide_age_selection_screen` feature flag, which is what the parents'
	 * in-funnel age screens are gated on.
	 */
	function isAgeCollected(): boolean {
		return state.value.appArea !== null
	}

	/** The age to seed the parent's model with, or null when there is nothing to seed. */
	function ageForParent(): string | null {
		return state.value.ageForParent
	}

	/**
	 * Adapt a parent funnel's config to the handoff, and return the new config string:
	 * drops the cloned entry screens, and works out the age in this config's own bucket
	 * vocabulary for `ageForParent()` to hand to the renderer.
	 *
	 * A no-op (returns the input untouched) unless a handoff into THIS appArea is armed,
	 * so a normal funnel load is unaffected.
	 */
	function adaptParentConfig(configString: string, appArea: AppArea | null | undefined, rawAge: unknown): string {
		if (!appArea || state.value.appArea !== appArea) return configString

		state.value = { ...state.value, ageForParent: ageForConfig(configString, rawAge) }
		return dropScreens(configString, state.value.hiddenScreens)
	}

	return { beginHandoff, endHandoff, isAgeCollected, ageForParent, adaptParentConfig }
}

// ── age ──────────────────────────────────────────────────────────────────────

/**
 * Express `rawAge` in `configString`'s own age vocabulary: the highest bucket the answer
 * reaches, returned as that bucket's exact string.
 *
 * Both halves are load-bearing:
 *   • TYPE — the store coerces `age` to a number (`EXTENTED_USER_MODEL_TYPES_MAP` tags it
 *     `'age'` → `parseInt`), while every `age` condition in every funnel compares against
 *     STRINGS with a strict `includes`, so a number matches nothing.
 *   • VOCABULARY — bucket sets differ between funnels (one pre-funnel offers 18/40/50/60/70,
 *     another 18/30/40/50). A `70` that no parent offers would fail every `['50','60']`
 *     check and drop the user into the younger-audience branch for the whole funnel.
 *
 * Falls back to the bare number as a string when the config offers no recognisable
 * buckets — that alone still repairs the number-vs-string mismatch.
 */
function ageForConfig(configString: string, rawAge: unknown): string | null {
	const age = toAgeNumber(rawAge)
	if (age === null) return null

	const buckets = ageBuckets(configString)
	if (!buckets.length) return String(age)

	const reached = buckets.filter((bucket) => bucket <= age)
	// Below the lowest bucket → the lowest one, mirroring how the gate's own bottom
	// option ("I'm 18-39") absorbs everyone younger.
	return String(reached.length ? reached[reached.length - 1] : buckets[0])
}

/** A raw answer can be `42`, `'42'` or `['42']`. */
function toAgeNumber(rawAge: unknown): number | null {
	const first = Array.isArray(rawAge) ? rawAge[0] : rawAge
	if (first === null || first === undefined || first === '') return null
	const age = Number.parseInt(String(first), 10)
	return Number.isFinite(age) ? age : null
}

/**
 * The numeric option values offered by the config's own age input, ascending. Legacy
 * inputs carry `options: [{ value }]`; a canvas `Select` holds `SelectItem` children each
 * with `props.value`. Non-numeric values (an i18n leaf, a `{ kind: 'binding' }`
 * placeholder) are not buckets and are skipped.
 */
function ageBuckets(configString: string): number[] {
	const ages = new Set<number>()

	eachRecord(parseConfig(configString), (record) => {
		if (!bindsAge(record)) return
		// Collect every `value` beneath the age-bound input.
		eachRecord(record, (inner) => {
			addAge(ages, inner.value)
			if (isRecord(inner.props)) addAge(ages, inner.props.value)
		})
	})

	return [...ages].sort((a, b) => a - b)
}

function addAge(ages: Set<number>, value: unknown): void {
	if (typeof value !== 'string' && typeof value !== 'number') return
	const age = Number.parseInt(String(value), 10)
	if (Number.isFinite(age)) ages.add(age)
}

/** A record that binds the `age` variable — canvas nodes use `props.variable`, legacy `variableName`. */
function bindsAge(record: Record<string, unknown>): boolean {
	return record.variableName === 'age' || (isRecord(record.props) && record.props.variable === 'age')
}

// ── screens ──────────────────────────────────────────────────────────────────

/**
 * Remove the screens whose `navigationParams.urlSlug` is in `slugs`, and return the new
 * config string. Defensive by design — the input is returned untouched on a parse error,
 * a missing `screens` array, or an empty/no-op result, so a bad slug list can never blank
 * out a funnel.
 */
function dropScreens(configString: string, slugs: string[]): string {
	if (!configString || !slugs.length) return configString

	const config = parseConfig(configString)
	if (!isRecord(config)) return configString

	const screens: unknown[] = Array.isArray(config.screens) ? config.screens : []
	if (!screens.length) return configString

	const hidden = new Set(slugs)
	const boundaries = progressBarBoundaries(config.progressBarData)
	const kept = screens.filter((screen) => {
		if (!isRecord(screen)) return true
		const slug = isRecord(screen.navigationParams) ? screen.navigationParams.urlSlug : undefined
		if (typeof slug !== 'string' || !hidden.has(slug)) return true
		// Slug matched → drop it, unless it bounds a progress-bar group.
		return typeof screen.id === 'string' && boundaries.has(screen.id)
	})

	if (!kept.length || kept.length === screens.length) return configString
	return JSON.stringify({ ...config, screens: kept })
}

/**
 * Screen ids that BOUND a progress-bar group (`start` / `finish`, across every variant
 * array). Those two are positional — the renderer resolves them with
 * `screenIds.indexOf(...)` and drops the whole group when either returns -1 — so a
 * boundary screen is kept even when its slug matched.
 *
 * `titles` / `segmentScreensOptions` keys are deliberately NOT boundaries: they are
 * per-screen-id lookup maps (a title override, `hideLogo` / `hideProgressBar` chrome
 * flags) read only for a screen that exists, so a stale key is inert. They are also
 * routinely authored for screens OUTSIDE the group's start..finish range — a parent's
 * entry screen typically has a `segmentScreensOptions` entry hiding the bar on it.
 * Counting those as boundaries vetoed exactly the removals this dedup exists for.
 */
function progressBarBoundaries(progressBarData: unknown): Set<string> {
	const ids = new Set<string>()
	if (!isRecord(progressBarData)) return ids

	for (const groups of Object.values(progressBarData)) {
		if (!Array.isArray(groups)) continue
		for (const group of groups) {
			if (!isRecord(group)) continue
			if (typeof group.start === 'string') ids.add(group.start)
			if (typeof group.finish === 'string') ids.add(group.finish)
		}
	}

	return ids
}

// ── shared helpers ───────────────────────────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
	return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function parseConfig(configString: string): unknown {
	try {
		return JSON.parse(configString)
	} catch {
		return null
	}
}

/** Depth-first walk visiting every plain-object node in an arbitrary config subtree. */
function eachRecord(node: unknown, visit: (record: Record<string, unknown>) => void): void {
	if (Array.isArray(node)) {
		for (const child of node) eachRecord(child, visit)
		return
	}
	if (!isRecord(node)) return
	visit(node)
	for (const child of Object.values(node)) eachRecord(child, visit)
}
